import { useState, useEffect, useRef, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { BaseballDiamond } from '@/components/BaseballDiamond';
import Fireworks from '@/components/Fireworks';

const API = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API.replace('https://', 'wss://').replace('http://', 'ws://') + '/api/ws';

/**
 * TV Preview / Simulator
 * 
 * Landscape-optimized display for Apple TV.
 * Shows everything EXCEPT answers during gameplay.
 */
export default function TVPreview() {
  // Session state
  const [sessionCode, setSessionCode] = useState(null);
  const [controllerConnected, setControllerConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  
  // Game state
  const [gameState, setGameState] = useState(null);
  const [currentSong, setCurrentSong] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [correctAnswers, setCorrectAnswers] = useState(null);
  const [showFireworks, setShowFireworks] = useState(false);
  const [runsScored, setRunsScored] = useState(0);
  
  // Audio & Timer
  const audioRef = useRef(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(30);
  const timerRef = useRef(null);
  
  // WebSocket
  const wsRef = useRef(null);
  
  // Playlists
  const [playlists, setPlaylists] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [selectedInnings, setSelectedInnings] = useState(3);

  // Fetch playlists
  useEffect(() => {
    fetch(`${API}/api/sessions/playlists`)
      .then(res => res.json())
      .then(data => {
        setPlaylists(data);
        if (data.length > 0) setSelectedPlaylist(data[0].id);
      })
      .catch(err => console.error('Failed to fetch playlists:', err));
  }, []);

  // Create session
  const createSession = async () => {
    try {
      const res = await fetch(`${API}/api/sessions/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          total_innings: selectedInnings,
          playlist_id: selectedPlaylist 
        })
      });
      const data = await res.json();
      setSessionCode(data.code);
      setGameState(data.state);
      connectWebSocket(data.code);
    } catch (err) {
      console.error('Failed to create session:', err);
    }
  };

  // WebSocket connection
  const connectWebSocket = useCallback((code) => {
    if (wsRef.current) wsRef.current.close();

    setConnectionStatus('connecting');
    const ws = new WebSocket(`${WS_URL}/sessions/${code}`);
    
    ws.onopen = () => {
      ws.send(JSON.stringify({ role: 'tv' }));
      setConnectionStatus('connected');
    };

    ws.onmessage = (event) => {
      const message = JSON.parse(event.data);
      console.log('TV received:', message);

      switch (message.event) {
        case 'connected':
          setGameState(message.state);
          break;
        case 'controller_connected':
          setControllerConnected(true);
          break;
        case 'controller_disconnected':
          setControllerConnected(false);
          break;
        case 'state_update':
        case 'game_started':
        case 'inning_changed':
          setGameState(message.state);
          setLastResult(null);
          setCorrectAnswers(null);
          setCurrentSong(null);
          setAudioPlaying(false);
          stopTimer();
          break;
        case 'pitch_started':
          setGameState(message.state);
          setCurrentSong(message.song);
          setLastResult(null);
          setCorrectAnswers(null);
          startAudioAndTimer(message.song?.preview_url);
          break;
        case 'answer_submitted':
          setGameState(message.state);
          setLastResult(message);
          setCorrectAnswers(message.correct_answers);
          setRunsScored(message.runs_scored || 0);
          setCurrentSong(null);
          stopAudioAndTimer();
          if (message.result === 'HOME_RUN') {
            setShowFireworks(true);
            setTimeout(() => setShowFireworks(false), 4000);
          }
          break;
        default:
          if (message.state) setGameState(message.state);
      }
    };

    ws.onerror = () => setConnectionStatus('error');
    ws.onclose = () => setConnectionStatus('disconnected');
    wsRef.current = ws;
  }, []);

  // Timer functions
  const startAudioAndTimer = (previewUrl) => {
    setTimeRemaining(30);
    setAudioPlaying(true);
    
    if (previewUrl && audioRef.current) {
      audioRef.current.src = previewUrl;
      audioRef.current.play().catch(e => console.log('Audio autoplay blocked:', e));
    }
    
    timerRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          stopTimer();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const stopAudioAndTimer = () => {
    stopTimer();
    setAudioPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
    }
  };

  // Cleanup
  useEffect(() => {
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (audioRef.current) audioRef.current.pause();
      stopTimer();
    };
  }, []);

  const getControllerUrl = () => `${window.location.origin}/controller/${sessionCode}`;

  // ==================== SCREENS ====================

  // Setup Screen
  if (!sessionCode) {
    return (
      <div className="min-h-screen bg-cream p-8 flex flex-col items-center justify-center">
        <h1 className="retro-heading text-4xl text-grass mb-2">RADIO BASEBALL</h1>
        <p className="retro-body text-xl text-dirt mb-8">TV Mode</p>
        
        <div className="pixel-card p-8 max-w-lg w-full">
          <label className="retro-body text-sm text-dirt block mb-2">PLAYLIST</label>
          <select 
            value={selectedPlaylist || ''}
            onChange={(e) => setSelectedPlaylist(e.target.value)}
            className="pixel-input w-full mb-6"
          >
            {playlists.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>

          <label className="retro-body text-sm text-dirt block mb-2">INNINGS</label>
          <div className="flex gap-4 justify-center mb-8">
            {[3, 5, 7, 9].map(num => (
              <button
                key={num}
                onClick={() => setSelectedInnings(num)}
                className={`retro-heading text-xl w-16 h-16 border-4 border-ink ${
                  selectedInnings === num ? 'bg-grass text-cream' : 'bg-white text-ink'
                }`}
              >
                {num}
              </button>
            ))}
          </div>

          <button onClick={createSession} className="pixel-btn pixel-btn-primary w-full text-lg">
            START GAME
          </button>
        </div>
      </div>
    );
  }

  // QR Code / Waiting Screen
  if (!controllerConnected || gameState?.phase === 'setup') {
    return (
      <div className="min-h-screen bg-cream p-8 flex flex-col items-center justify-center">
        <h1 className="retro-heading text-4xl text-grass mb-8">RADIO BASEBALL</h1>
        
        <div className="pixel-card p-8 text-center">
          <p className="retro-body text-sm text-dirt mb-4">SCAN TO JOIN</p>
          <div className="bg-white p-4 inline-block border-4 border-ink mb-4">
            <QRCodeSVG value={getControllerUrl()} size={200} bgColor="#ffffff" fgColor="#1a1a1a" />
          </div>
          <p className="retro-heading text-3xl text-ink mb-2">
            CODE: <span className="text-grass">{sessionCode}</span>
          </p>
        </div>

        <div className={`mt-8 px-8 py-4 border-4 border-ink ${controllerConnected ? 'bg-grass' : 'bg-dirt'}`}>
          <p className="retro-heading text-lg text-cream">
            {controllerConnected ? '✓ CONTROLLER CONNECTED' : 'WAITING FOR CONTROLLER...'}
          </p>
        </div>
      </div>
    );
  }

  // Game Over Screen
  if (gameState?.phase === 'game_over') {
    const winner = gameState.team_a.score > gameState.team_b.score 
      ? gameState.team_a.name 
      : gameState.team_b.score > gameState.team_a.score 
        ? gameState.team_b.name : 'TIE';

    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <h1 className="retro-heading text-5xl text-dirt mb-8">GAME OVER</h1>
          <div className="pixel-card p-8 inline-flex items-center gap-12 mb-8">
            <div className="text-center">
              <p className="retro-body text-xl text-ink mb-2">{gameState.team_a.name}</p>
              <p className="retro-heading text-6xl text-grass">{gameState.team_a.score}</p>
            </div>
            <p className="retro-heading text-3xl text-dirt">VS</p>
            <div className="text-center">
              <p className="retro-body text-xl text-ink mb-2">{gameState.team_b.name}</p>
              <p className="retro-heading text-6xl text-grass">{gameState.team_b.score}</p>
            </div>
          </div>
          <div className="pixel-card p-6 bg-grass inline-block">
            <p className="retro-heading text-3xl text-cream">
              {winner === 'TIE' ? "IT'S A TIE!" : `${winner} WINS!`}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Inning Summary
  if (gameState?.phase === 'inning_summary') {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <h1 className="retro-heading text-4xl text-dirt mb-2">
            END OF {gameState.half === 'top' ? 'TOP' : 'BOTTOM'}
          </h1>
          <h2 className="retro-heading text-3xl text-grass mb-8">INNING {gameState.inning}</h2>
          <div className="pixel-card p-6 min-w-[500px]">
            <div className="flex justify-between p-4 bg-dirt border-b-4 border-ink">
              <span className="retro-heading text-xl text-cream">TEAM</span>
              <span className="retro-heading text-xl text-cream">RUNS</span>
            </div>
            <div className="flex justify-between p-4 border-b-2 border-dirt">
              <span className="retro-body text-xl text-ink">{gameState.team_a.name}</span>
              <span className="retro-heading text-3xl text-grass">{gameState.team_a.score}</span>
            </div>
            <div className="flex justify-between p-4">
              <span className="retro-body text-xl text-ink">{gameState.team_b.name}</span>
              <span className="retro-heading text-3xl text-grass">{gameState.team_b.score}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Result Screen
  if (lastResult && gameState?.phase === 'result') {
    const resultColors = {
      'OUT': 'text-red-500',
      'SINGLE': 'text-yellow-500',
      'DOUBLE': 'text-blue-500',
      'TRIPLE': 'text-purple-500',
      'HOME_RUN': 'text-grass'
    };

    return (
      <div className="min-h-screen bg-cream flex items-center justify-center relative">
        {showFireworks && <Fireworks />}
        
        <div className="text-center relative z-10">
          <div className={`retro-heading text-8xl mb-4 ${resultColors[lastResult.result] || 'text-ink'}`}>
            {lastResult.result.replace('_', ' ')}
          </div>
          
          {runsScored > 0 && (
            <div className="pixel-card p-4 bg-grass inline-block mb-6">
              <p className="retro-heading text-3xl text-cream">
                +{runsScored} RUN{runsScored > 1 ? 'S' : ''}!
              </p>
            </div>
          )}

          {correctAnswers && (
            <div className="pixel-card p-6 text-left max-w-xl mx-auto">
              <p className="retro-body text-sm text-dirt mb-3">THE SONG WAS:</p>
              <p className="retro-heading text-lg text-ink">{correctAnswers.title}</p>
              <p className="retro-body text-lg text-dirt">by {correctAnswers.artist}</p>
              <p className="retro-body text-md text-dirt mt-2">{correctAnswers.album} ({correctAnswers.year})</p>
            </div>
          )}

          <div className="mt-8">
            <p className="retro-heading text-xl text-ink">
              {gameState.team_a.name}: {gameState.team_a.score} | {gameState.team_b.name}: {gameState.team_b.score}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ==================== MAIN GAME SCREEN (LANDSCAPE) ====================
  const isTopInning = gameState?.half === 'top';
  const battingTeam = isTopInning ? gameState?.team_a : gameState?.team_b;
  const pitchingTeam = isTopInning ? gameState?.team_b : gameState?.team_a;
  const currentBatter = battingTeam?.players?.[gameState?.current_batter_index] || 'Player';

  return (
    <div className="min-h-screen bg-grass flex flex-col" style={{background: 'linear-gradient(180deg, #4a7c4e 0%, #3d6b41 100%)'}}>
      <audio ref={audioRef} />
      
      {/* ========== TOP SCOREBOARD - Matches Mobile Style ========== */}
      <div className="bg-cream border-b-4 border-ink">
        <div className="flex items-center justify-between px-8 py-4">
          {/* Team A Score Box */}
          <div className="flex items-center gap-4">
            <span className="retro-body text-2xl text-ink">{gameState?.team_a?.name || 'A'}</span>
            <div className="bg-ink px-6 py-3 border-4 border-ink">
              <span className="retro-heading text-5xl" style={{color: '#00ff00', textShadow: '0 0 15px #00ff00'}}>
                {gameState?.team_a?.score || 0}
              </span>
            </div>
            <span className="retro-body text-xl text-ink">
              {isTopInning ? '▲' : ''} {gameState?.inning || 1}/{gameState?.total_innings || 3}
            </span>
          </div>

          {/* Outs Indicator */}
          <div className="flex items-center gap-3">
            <span className="retro-body text-xl text-ink">OUTS:</span>
            <div className="flex gap-2">
              {[0, 1, 2].map(i => (
                <div 
                  key={i}
                  className={`w-8 h-8 rounded-full border-4 border-ink ${
                    i < (gameState?.outs || 0) ? 'bg-ink' : 'bg-white'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Team B Score Box */}
          <div className="flex items-center gap-4">
            <span className="retro-body text-xl text-ink">
              {!isTopInning ? '▼' : ''} {!isTopInning ? `${gameState?.inning || 1}/${gameState?.total_innings || 3}` : ''}
            </span>
            <div className="bg-ink px-6 py-3 border-4 border-ink">
              <span className="retro-heading text-5xl" style={{color: '#00ff00', textShadow: '0 0 15px #00ff00'}}>
                {gameState?.team_b?.score || 0}
              </span>
            </div>
            <span className="retro-body text-2xl text-ink">{gameState?.team_b?.name || 'B'}</span>
          </div>
        </div>
      </div>

      {/* ========== MAIN CONTENT AREA ========== */}
      <div className="flex-1 flex">
        
        {/* LEFT PANEL - Baseball Diamond */}
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="bg-cream border-4 border-ink p-6" style={{minWidth: '350px'}}>
            <BaseballDiamond bases={gameState?.bases || {}} size="large" />
          </div>
        </div>

        {/* RIGHT PANEL - Game State Info */}
        <div className="flex-1 flex flex-col justify-center p-6">
          
          {/* Inning Status Bar */}
          <div className="bg-cream border-4 border-ink mb-6">
            <div className="flex justify-between items-center px-6 py-3">
              <span className="retro-body text-xl text-ink">
                {isTopInning ? 'TOP' : 'BOTTOM'} {gameState?.inning || 1}
              </span>
              <span className="retro-heading text-xl text-dirt">
                {battingTeam?.name} BATTING
              </span>
            </div>
          </div>

          {audioPlaying ? (
            /* ========== SONG PLAYING STATE ========== */
            <div className="bg-cream border-4 border-ink p-6">
              {/* Pitching/Batting Info */}
              <div className="border-4 border-dirt bg-dirt/10 p-4 mb-4">
                <p className="retro-body text-sm text-dirt">{pitchingTeam?.name} PITCHING</p>
                <div className="flex justify-between items-center">
                  <p className="retro-heading text-2xl text-dirt">{battingTeam?.name} batting</p>
                  <div className="flex items-center gap-2">
                    <span className="retro-body text-lg text-dirt">♪</span>
                    <span className="retro-heading text-4xl" style={{color: '#00aa00'}}>
                      {timeRemaining}s
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Progress Bar */}
              <div className="w-full h-8 bg-ink border-4 border-ink mb-6">
                <div 
                  className="h-full transition-all duration-1000"
                  style={{ 
                    width: `${((30 - timeRemaining) / 30) * 100}%`,
                    backgroundColor: '#00ff00'
                  }}
                />
              </div>

              {/* Answer Categories - NO ANSWERS SHOWN */}
              <p className="retro-body text-center text-sm text-dirt mb-4">
                Waiting for {battingTeam?.name}&apos;s guesses:
              </p>
              
              <div className="space-y-3">
                {['TITLE', 'ARTIST', 'ALBUM', 'YEAR'].map((category) => (
                  <div key={category} className="border-4 border-ink bg-white p-4">
                    <p className="retro-body text-xs text-dirt mb-1">{category}</p>
                    <p className="retro-heading text-xl text-ink">? ? ?</p>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* ========== WAITING/READY STATE ========== */
            <div className="bg-cream border-4 border-ink p-6">
              {/* Pitching Banner */}
              <div className="border-4 border-dirt bg-dirt/10 p-6 mb-4 text-center">
                <div className="retro-heading text-4xl text-dirt mb-2">{pitchingTeam?.name}</div>
                <p className="retro-body text-lg text-dirt">YOU ARE PITCHING - HOLD THE PHONE</p>
              </div>

              {/* Baseball Icon */}
              <div className="flex justify-center my-6">
                <div className="w-20 h-20 rounded-full bg-white border-4 border-ink flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full border-2 border-red-400" style={{
                    background: 'white',
                    boxShadow: 'inset 0 0 10px rgba(0,0,0,0.1)'
                  }}>
                    <svg viewBox="0 0 100 100" className="w-full h-full">
                      <path d="M20 30 Q50 50 20 70" fill="none" stroke="#dc2626" strokeWidth="3"/>
                      <path d="M80 30 Q50 50 80 70" fill="none" stroke="#dc2626" strokeWidth="3"/>
                    </svg>
                  </div>
                </div>
              </div>

              {/* Now Batting Banner */}
              <div className="border-4 border-grass bg-grass/10 p-6 text-center">
                <p className="retro-body text-sm text-grass mb-1">NOW BATTING</p>
                <div className="retro-heading text-4xl text-grass mb-1">{currentBatter}</div>
                <p className="retro-body text-lg text-grass">({battingTeam?.name})</p>
              </div>

              {/* Instruction */}
              <p className="retro-body text-center text-lg text-ink mt-6">
                Press PITCH when {currentBatter} is ready!
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ========== BOTTOM STATUS BAR ========== */}
      <div className="bg-cream border-t-4 border-ink">
        <div className="flex justify-between items-center px-8 py-3">
          <span className="retro-body text-lg text-ink">
            {isTopInning ? 'TOP' : 'BOTTOM'} {gameState?.inning || 1}
          </span>
          <span className="retro-heading text-xl text-grass">
            RADIO BASEBALL
          </span>
          <span className={`retro-body text-sm ${controllerConnected ? 'text-grass' : 'text-red-500'}`}>
            {controllerConnected ? '● CONTROLLER CONNECTED' : '○ WAITING FOR CONTROLLER'}
          </span>
        </div>
      </div>
    </div>
  );
}
